import { motion } from 'framer-motion';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useEffect } from 'react';
import { useSEO } from '../../utils/seo';

const ContactPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://danvanremovals.co.uk';
  
  useSEO({
    title: 'Contact Us | Get Free Quote | Dan & Van Removals Lancashire',
    description: 'Get your free removals quote. Call 07530 166035 or message us on WhatsApp. 24hr response time. Serving Lancashire with professional moving services.',
    keywords: 'contact removals Lancashire, free quote, removals quote, Dan Van contact, moving quote Lancashire',
    canonical: '/contact',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: 'Contact Dan & Van Removals',
      description: 'Get in touch with Dan & Van Removals for a free, no-obligation quote.',
      mainEntity: {
        '@type': 'MovingCompany',
        name: 'Dan & Van Removals',
        telephone: '07530166035',
        areaServed: 'Lancashire, UK',
      },
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative bg-white py-16 sm:py-20 md:py-24 mt-20 border-t-4 border-gold">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-sans text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-charcoal mb-4 sm:mb-6">
              Get your free quote
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-gray-700">
              Message us on WhatsApp or give us a call - we'll get back to you within 24 hours
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12">
            {/* Contact Methods */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-white border border-gold/20 rounded-lg p-6 sm:p-8 lg:p-12 shadow-luxury"
              >
                <h2 className="font-sans text-2xl sm:text-3xl font-bold text-charcoal mb-6">
                  Contact us directly
                </h2>

                <div className="space-y-6 mb-8">
                  <div>
                    <div className="text-sm text-gray-600 mb-2">Phone</div>
                    <a
                      href="tel:07530166035"
                      className="text-3xl font-sans font-bold text-charcoal hover:text-gold transition-colors cursor-pointer"
                    >
                      07530 166035
                    </a>
                  </div>

                  <div className="pt-6 border-t border-gray-200">
                    <div className="text-sm text-gray-600 mb-3">Quick Contact</div>
                    <div className="space-y-3">
                      <a
                        href="https://wa.me/447530166035"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center w-full py-4 text-lg bg-green-600 text-white font-semibold rounded hover:bg-green-700 transition-all whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-whatsapp-line mr-2 text-2xl"></i>
                        Message on WhatsApp
                      </a>
                      <a
                        href="tel:07530166035"
                        className="flex items-center justify-center w-full py-4 text-lg border-2 border-charcoal text-charcoal font-semibold rounded hover:border-gold hover:text-gold transition-all whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-phone-line mr-2 text-xl"></i>
                        Call Now
                      </a>
                    </div>
                  </div>

                  <div className="pt-6 border-t border-gray-200">
                    <div className="text-sm text-gray-600 mb-2">Opening Hours</div>
                    <p className="text-base text-charcoal">
                      Monday - Saturday
                      <br />
                      8:00am - 6:00pm
                    </p>
                  </div>

                  <div className="pt-6 border-t border-gray-200">
                    <div className="text-sm text-gray-600 mb-2">Service Area</div>
                    <p className="text-base text-charcoal">Lancashire, UK</p>
                  </div>
                </div>
              </motion.div>

              {/* Trust Badges */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="mt-6 sm:mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6"
              >
                <div className="flex items-center">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-gold/10 rounded-full mr-3 sm:mr-4 flex-shrink-0">
                    <i className="ri-shield-check-line text-xl sm:text-2xl text-gold"></i>
                  </div>
                  <div>
                    <h3 className="font-sans font-bold text-sm sm:text-base text-charcoal">Fully Insured</h3>
                    <p className="text-xs sm:text-sm text-gray-600">Complete protection</p>
                  </div>
                </div>

                <div className="flex items-center">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-gold/10 rounded-full mr-3 sm:mr-4 flex-shrink-0">
                    <i className="ri-time-line text-xl sm:text-2xl text-gold"></i>
                  </div>
                  <div>
                    <h3 className="font-sans font-bold text-sm sm:text-base text-charcoal">24hr Response</h3>
                    <p className="text-xs sm:text-sm text-gray-600">Quick turnaround</p>
                  </div>
                </div>

                <div className="flex items-center">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-gold/10 rounded-full mr-3 sm:mr-4 flex-shrink-0">
                    <i className="ri-file-list-3-line text-xl sm:text-2xl text-gold"></i>
                  </div>
                  <div>
                    <h3 className="font-sans font-bold text-sm sm:text-base text-charcoal">No Obligation</h3>
                    <p className="text-xs sm:text-sm text-gray-600">Free quotes</p>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Info Card */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="sticky top-24 space-y-6"
              >
                <div className="bg-charcoal text-white rounded-lg p-6 sm:p-8">
                  <h3 className="font-sans text-xl sm:text-2xl font-bold mb-4">
                    Why choose WhatsApp?
                  </h3>
                  <ul className="space-y-3 text-gray-300 text-sm sm:text-base">
                    <li className="flex items-start">
                      <i className="ri-check-line text-gold text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                      <span>Instant responses during business hours</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-gold text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                      <span>Send photos of items you need moved</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-gold text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                      <span>Get accurate quotes faster</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-gold text-xl mr-3 flex-shrink-0 mt-0.5"></i>
                      <span>Easy to track conversation history</span>
                    </li>
                  </ul>
                  <a
                    href="https://wa.me/447530166035"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full mt-6 py-3 text-center bg-gold text-charcoal font-semibold rounded hover:shadow-luxury-hover transition-all whitespace-nowrap cursor-pointer"
                  >
                    Start WhatsApp Chat
                  </a>
                </div>

                <div className="bg-gray-50 rounded-lg p-6 sm:p-8">
                  <h4 className="font-sans text-lg sm:text-xl font-bold text-charcoal mb-3">
                    Need help choosing a service?
                  </h4>
                  <p className="text-gray-700 text-sm sm:text-base mb-4">
                    Not sure which service you need? Give us a call and we'll help you find the right solution for your move.
                  </p>
                  <a
                    href="tel:07530166035"
                    className="block w-full py-3 text-center border-2 border-charcoal text-charcoal font-semibold rounded hover:border-gold hover:text-gold transition-all whitespace-nowrap cursor-pointer"
                  >
                    Speak to Our Team
                  </a>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-12 sm:py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="font-sans text-2xl sm:text-3xl md:text-4xl font-bold text-charcoal mb-4">
              Serving Lancashire & Beyond
            </h2>
            <p className="text-base sm:text-lg text-gray-700">
              Professional removals across the region
            </p>
          </div>

          <div className="bg-white rounded-lg overflow-hidden shadow-luxury">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d151234.56789!2d-2.7044!3d53.7632!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x487b9f0f0f0f0f0f%3A0x0!2sLancashire%2C%20UK!5e0!3m2!1sen!2suk!4v1234567890"
              width="100%"
              height="350"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Lancashire service area map"
              className="sm:h-[450px]"
            ></iframe>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;